/* Dedicated, visitor-safe exhibition controls and clearly labelled teaching examples. */
function isExhibitionKiosk() {
  const params = new URLSearchParams(window.location.search);
  return params.get('exhibit') === '1' && params.get('teacher') !== '1';
}

function isExhibitionSample(item) {
  return item?.isDemo === true || /^sh-\d+$/.test(item?.id || '');
}

const EXHIBITION_SAMPLE_PROCESS = {
  'sh-1': {
    question: '教学示例：怎样让没来过宁夏的人，一眼看懂黄河、平原与山地的位置关系？',
    guidance: '示例引导：先确定地图方向，再寻找河流与山地的参照关系。追问：颜色和高度分别表达什么？',
    revision: '示例修改：把“把沙堆得更漂亮”改为“先用不同颜色区分河流、山地和平原，再添加图例”。',
    result: '预期成果：一件有方向标、图例与口头解释的地形沙盘。本案例暂无真实学生作品附件。',
  },
  'sh-2': {
    question: '教学示例：一座模型塔怎样才能既有层次，又能稳稳站立？',
    guidance: '示例引导：把结构拆成底座、塔身与连接处；设计一个只改变底座宽度的对比实验。',
    revision: '示例修改：先画草图，记录第一次倾斜的位置，再调整重心与底座，而不是一次做完所有层。',
    result: '预期成果：塔模型、两版设计草图和稳定性实验记录。材料配比需要实际测试，不把示例当施工标准。',
  },
  'sh-3': {
    question: '教学示例：如何在一分钟里，让别人理解一件沙主题作品的价值？',
    guidance: '示例引导：先说“为谁解决什么问题”，再用具体功能与证据说明，不只堆砌赞美词。',
    revision: '示例修改：把长篇介绍压缩成“问题—方案—证据—邀请”四句话，给同伴试讲并修改。',
    result: '预期成果：一分钟讲解稿、展板草图与同伴反馈表。这里展示的是教学脚本，不是实际路演录像。',
  },
  'sh-4': {
    question: '教学示例：不用画面，怎样让听众听见“风穿过院子”的故事？',
    guidance: '示例引导：列出环境声、动作声、旁白三条声音线索；比较有无停顿时的感受。',
    revision: '示例修改：删除重复旁白，让沙铃和脚步先讲述场景，在情绪转折处留出安静的间隙。',
    result: '预期成果：声音分镜与一段原创录音。本案例尚无可播放的真实学生录音。',
  },
  'sh-5': {
    question: '教学示例：九个小组的作品不同，怎样让整条走廊看起来既统一又各有特色？',
    guidance: '示例引导：分别找出需要统一的元素与可以变化的元素，检查参观动线和阅读距离。',
    revision: '示例修改：统一标题与边框，给各组保留一种主题色；把过小的说明文字改为近看细节、远看主题。',
    result: '预期成果：走廊布展方案、九张主题门草图与观众动线图。效果插图不代表已经布展。',
  },
};

function exhibitionPublicName(item) {
  return isExhibitionSample(item) ? '教学示例 · 非真实学生记录' : (String(item.displayName || '').trim() || '探索小组');
}

function exhibitionWorkProcess(item) {
  return isExhibitionSample(item) ? { ...(EXHIBITION_SAMPLE_PROCESS[item.id] || {}), ...(item.process || {}) } : (item.process || {});
}

function exhibitionMediaSource(media) {
  const value = String(media?.url || media?.data || '');
  if (/^data:(image\/(?:png|jpe?g|webp|gif|avif)|audio\/[\w.+-]+|video\/[\w.+-]+|application\/pdf|text\/plain)[;,]/i.test(value)) return value;
  try {
    const url = new URL(value, window.location.href);
    if (value && /^https?:$/.test(url.protocol)) return url.href;
  } catch (_) {}
  return '';
}

function ExhibitionWorkProcess({ item }) {
  const process = exhibitionWorkProcess(item);
  return <section className="exhibit-work-process" aria-label="作品的学习过程">
    <div className="exhibit-process-heading"><h3>从好问题，到自己的答案。</h3><span className="chip">{isExhibitionSample(item) ? '预置教学脚本 · 非现场 AI 回复' : '经教师审阅的学习过程'}</span></div>
    <div className="exhibit-process-grid">{[['question','最初的问题'],['guidance','AI 如何引导'],['revision','学生如何修改'],['result','最终成果']].map(([key, label], index) => <div key={key}><span>0{index + 1}</span><h4>{label}</h4><p>{process[key] || '此过程尚未补充，暂不作推断。'}</p></div>)}</div>
  </section>;
}

const EXHIBITION_GUIDE_STEPS = [
  { title: '从一粒沙，向世界提问', label: '认识项目', roleId: 'sand-table', text: '九位跨学科导师、八种学习工具、学问思辨行五个阶段，帮助孩子把好奇变成自己的作品。', prompt: '讲解提示：先请观众说出一个关于沙的问题，再介绍“AI 引导思考，孩子做出判断”。' },
  { title: '把大问题，变成可研究的问题', label: '地形沙盘案例', roleId: 'sand-table', caseId: 'sh-1', field: 'question', prompt: '可以问观众：如果不用一大段文字，你会用什么方法解释山、河流和平原的位置？' },
  { title: 'AI 的价值，是把思路打开', label: '模型工程案例', roleId: 'tower', caseId: 'sh-2', field: 'guidance', prompt: '讲解重点：工具可以帮忙拆解问题、提出对比实验，但结构是否稳定，要靠实际测试。' },
  { title: '让学生的选择与修改，被看见', label: '声音剧场案例', roleId: 'sound', caseId: 'sh-4', field: 'revision', prompt: '讲解重点：评价的不只是成品，还包括孩子为何删改、如何听取意见、怎样解释选择。' },
  { title: '提交、审核、发布，形成完整记录', label: '学习闭环', roleId: 'sand-art', text: '学生提交作品与附件，教师查看、反馈和审核；确认公开授权后，用展示署名发布到作品展厅。', prompt: '讲解提示：真实姓名和私人对话不会作为默认公开内容。教学示例与真实作品始终分开标记。' },
  { title: '现在，开始你的三分钟探索', label: '动手体验', roleId: 'invest', text: '选一位导师，提出问题，选择一种学习工具，再看看作品背后的学习过程。现场 AI 需要联网，教学示例可以离线讲解。', prompt: '操作提示：结束体验后点击“下一位访客”，会清除本机的游客对话并退出登录，不会删除学生作品。' },
];

function ExhibitionGuidedTour({ onClose, onAgent, onOpenShowcase }) {
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState(true);
  const stepIndex = Math.min(5, Math.floor(seconds / 30));
  const step = EXHIBITION_GUIDE_STEPS[stepIndex];
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => setSeconds(value => {
      if (value >= 179) { setPlaying(false); return 180; }
      return value + 1;
    }), 1000);
    return () => clearInterval(timer);
  }, [playing]);
  const navigate = index => setSeconds(Math.max(0, Math.min(5, index)) * 30);
  return <div className="exhibit-tour">
    <div className="exhibit-tour-top"><div><div className="exhibit-eyebrow">180 SECONDS / EXHIBITION GUIDE</div><h2>三分钟，读懂一沙一世界</h2></div><button className="sand-button sand-button-ghost" onClick={onClose} aria-label="关闭三分钟导览"><ExhibitionIcon name="close" size={18}/></button></div>
    <p className="exhibit-example-notice">本导览使用预置教学脚本与主题插图，不是真实学生作品，也不是现场 AI 回复。无需联网即可播放。</p>
    <div className="exhibit-tour-scene"><div className="exhibit-tour-art"><img src={`assets/exhibition/roles/${step.roleId}.jpg`} alt={`${ROLES_BY_ID[step.roleId].roleName}主题插图`}/><span>主题插图</span></div><div className="exhibit-tour-copy" aria-live={playing ? 'off' : 'polite'}><span className="chip gold">0{stepIndex + 1} / {step.label}</span><h3>{step.title}</h3><p>{step.text || EXHIBITION_SAMPLE_PROCESS[step.caseId][step.field]}</p><aside>{step.prompt}</aside></div></div>
    <div className="exhibit-tour-progress" aria-label={`导览进度，${seconds} 秒，共 180 秒`}><span style={{ width: `${seconds / 180 * 100}%` }}/></div>
    <div className="exhibit-tour-controls"><div><button className="sand-button sand-button-ghost" disabled={!stepIndex} onClick={() => navigate(stepIndex - 1)}>上一步</button><button className="sand-button" onClick={() => { if (seconds >= 180) setSeconds(0); setPlaying(value => !value); }}>{seconds >= 180 ? '重新播放' : playing ? '暂停导览' : '继续导览'}</button><button className="sand-button sand-button-ghost" disabled={stepIndex === 5} onClick={() => navigate(stepIndex + 1)}>下一步</button></div><span>{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')} / 03:00</span></div>
    <div className="exhibit-tour-exits"><button className="nav-text" onClick={() => { onClose(); onAgent?.(step.roleId); }}>与这位导师实际对话 →</button><button className="nav-text" onClick={() => { onClose(); onOpenShowcase?.(); }}>参观作品与教学示例 →</button></div>
  </div>;
}

function ExhibitionControls({ state, view, onHome, onNextVisitor, onOpenShowcase, onAgent, idleSeconds = 120 }) {
  const kiosk = isExhibitionKiosk();
  const [tourOpen, setTourOpen] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const lastActivity = useRef(Date.now());
  const resetLock = useRef(false);
  const resetVisitor = useCallback(async () => {
    if (resetLock.current) return;
    resetLock.current = true; setResetting(true); setResetError('');
    try { await onNextVisitor?.(); setTourOpen(false); setRemaining(null); }
    catch (_) { setResetError('暂时未能结束体验，请重试。当前学习作品没有被删除。'); }
    finally { resetLock.current = false; setResetting(false); lastActivity.current = Date.now(); }
  }, [onNextVisitor]);
  useEffect(() => {
    if (!kiosk) return;
    document.body.classList.add('exhibit-kiosk');
    return () => document.body.classList.remove('exhibit-kiosk');
  }, [kiosk]);
  useEffect(() => { lastActivity.current = Date.now(); setRemaining(null); }, [view, tourOpen]);
  useEffect(() => {
    if (!kiosk || view === 'teacher' || tourOpen) return;
    const active = event => {
      // Buttons in the countdown dialog must receive their click before the dialog closes.
      if (event?.target?.closest?.('.exhibit-idle-prompt')) return;
      lastActivity.current = Date.now(); setRemaining(null);
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, active, { passive: true }));
    const timer = setInterval(() => {
      // Fullscreen gallery playback is intentional activity, not an abandoned visitor session.
      if (document.querySelector('[data-exhibit-auto-playing="true"]')) { active(); return; }
      const left = Math.ceil(Math.max(0, idleSeconds * 1000 - (Date.now() - lastActivity.current)) / 1000);
      if (view === 'landing' && !state?.session && !Object.values(state?.anonChats || {}).some(x => x?.length)) return;
      setRemaining(left <= 10 ? left : null);
      if (!left) { active(); resetVisitor(); }
    }, 1000);
    return () => { clearInterval(timer); events.forEach(event => window.removeEventListener(event, active)); };
  }, [kiosk, view, tourOpen, idleSeconds, resetVisitor, state?.session, state?.anonChats]);
  if (!kiosk || view === 'teacher') return null;
  return <Fragment>
    <nav className="exhibit-kiosk-controls" aria-label="展览体验控制"><div className="exhibit-kiosk-label"><span className="status-dot"/><b>展览体验</b><small>现场 AI 需联网</small></div><div><button onClick={onHome} className="sand-button sand-button-ghost">首页</button><button onClick={() => setTourOpen(true)} className="sand-button sand-button-ghost">三分钟导览</button><button onClick={resetVisitor} disabled={resetting} className="sand-button" title="仅清除游客对话并退出登录，保留学生作品">{resetting ? '正在结束…' : '下一位访客'}</button></div></nav>
    {resetError && <div className="exhibit-reset-error" role="alert">{resetError}<button onClick={() => setResetError('')} aria-label="关闭提示">×</button></div>}
    <Modal open={remaining !== null && !tourOpen} onClose={() => { lastActivity.current = Date.now(); setRemaining(null); }} maxW="max-w-md"><div className="exhibit-idle-prompt"><div className="exhibit-eyebrow">READY FOR THE NEXT EXPLORER</div><h2>还在体验吗？</h2><p>由于一段时间没有操作，将在 <strong aria-live="polite">{remaining}</strong> 秒后退出登录、清除游客对话并回到首页。</p><p>学生学习记录与已发布作品都会保留。</p><button className="sand-button" onClick={() => { lastActivity.current = Date.now(); setRemaining(null); }}>我还在，继续体验</button><button className="nav-text" onClick={resetVisitor}>现在结束体验</button></div></Modal>
    <Modal open={tourOpen} onClose={() => setTourOpen(false)} maxW="max-w-5xl">{tourOpen && <ExhibitionGuidedTour onClose={() => setTourOpen(false)} onAgent={onAgent} onOpenShowcase={onOpenShowcase}/>}</Modal>
  </Fragment>;
}
